import React from "react";
import { Flex } from "rebass/styled-components";
import styled, { css, keyframes } from "styled-components";
import { HAPPY_PATH_EVENTS } from "../../@types";
import type { HappyPathEvent } from "../../@types";

type StepperProps = {
  activeIdx: number;
  isError: boolean;
  fillDuration: number;
};

export const Stepper = ({ activeIdx, isError, fillDuration }: StepperProps) => {
  const lastIdx = HAPPY_PATH_EVENTS.length - 1;

  return (
    <Track>
      {HAPPY_PATH_EVENTS.map((step, i) => {
        const isPast = i < activeIdx;
        const isActive = i === activeIdx && !isError;
        const isErrorNode = i === activeIdx && isError;

        const dotNode = (
          <DotWrapper
            key={`dot-${step}`}
            aria-label={`${STEP_LABELS[step]} — ${
              isErrorNode
                ? "error"
                : isActive
                ? "active"
                : isPast
                ? "complete"
                : "pending"
            }`}
          >
            {isActive && <Halo />}
            <Dot $past={isPast} $active={isActive} $error={isErrorNode} />
          </DotWrapper>
        );

        if (i === 0) return dotNode;

        const segIdx = i - 1;
        const connFilled = i <= activeIdx;
        const connAnimating =
          !connFilled && i === activeIdx + 1 && activeIdx < lastIdx && !isError;

        return (
          <React.Fragment key={step}>
            <ConnectorWrap $weight={segIdx < 3 ? 1 : 2.5}>
              <ConnectorBg />
              <ConnectorFill
                $active={connFilled || connAnimating}
                $instant={connFilled}
                $duration={fillDuration}
                $error={isErrorNode}
              />
            </ConnectorWrap>
            {dotNode}
          </React.Fragment>
        );
      })}
    </Track>
  );
};

const STEP_LABELS: Record<HappyPathEvent, string> = {
  REQUESTED: "Requested",
  CREATING: "Creating",
  SUBMITTED: "Submitted",
  QUEUED: "Queued",
  CAPTURING: "Recording",
  COMPLETED: "Complete",
};

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.35); opacity: 0.7; }
`;

const Track = styled(Flex)`
  align-items: center;
  width: 100%;
  padding: 6px 0;
`;

const DotWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

const haloBreath = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(2); opacity: 0; }
`;

const Halo = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.envyAlpha30};
  animation: ${haloBreath} 2s ease-in-out infinite;
  pointer-events: none;
`;

const Dot = styled.div<{
  $past: boolean;
  $active: boolean;
  $error: boolean;
}>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  position: relative;
  z-index: 1;
  background-color: ${({ $past, $active, $error, theme }) =>
    $error
      ? theme.colors.alert
      : $past || $active
      ? theme.colors.envy
      : "transparent"};
  border: 2px solid
    ${({ $past, $active, $error, theme }) =>
      $error
        ? theme.colors.alert
        : $past || $active
        ? theme.colors.envy
        : theme.colors.chalkAlpha40};
  transition:
    background-color 0.3s,
    border-color 0.3s;
  ${({ $active }) =>
    $active &&
    css`
      animation: ${pulse} 2s ease-in-out infinite;
    `}
`;

const ConnectorWrap = styled.div<{ $weight: number }>`
  position: relative;
  flex: ${({ $weight }) => $weight};
  height: 2px;
  min-width: 6px;
`;

const ConnectorBg = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 1px;
`;

const ConnectorFill = styled.div<{
  $active: boolean;
  $instant: boolean;
  $duration: number;
  $error: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ $active }) => ($active ? "100%" : "0%")};
  background-color: ${({ $error, theme }) =>
    $error ? theme.colors.alert : theme.colors.envy};
  border-radius: 1px;
  transition: ${({ $instant, $duration }) =>
    $instant ? "none" : `width ${$duration}s ease-in-out`};
  ${({ $active, $instant, $error, theme }) =>
    $active &&
    !$instant &&
    !$error &&
    css`
      box-shadow: 0 0 6px 1px ${theme.colors.envyAlpha30};
    `}
`;
